// Purchase manager's "Update Purchase Request" side drawer (Edit on a pending /
// ordered request in PurchasePanel). The request is read-only; the only status
// it offers is "Delivering to <requester> (<role>)", see purchaseDelivery.js.
import React, { useState } from 'react';
import { X } from 'lucide-react';
import showToast from '../../../utils/toast';
import { deliveringLabel, markDelivering, requesterWithRole } from './purchaseDelivery';
import './UpdatePurchaseRequestDrawer.css';

const CURRENT_STATUS_LABEL = { pending: 'Pending', ordered: 'Ordered' };
const DELIVERING = 'delivering';

const formatDate = (date) => {
    if (!date) return 'N/A';
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleDateString('en-IN');
};

const getBalagruhaName = (request, balagruhas) => {
    const id = (request.balagruhaId?._id || request.balagruhaId || '').toString();
    if (request.balagruhaId?.name) return request.balagruhaId.name;
    if (request.balagruhaName) return request.balagruhaName;
    if (id === 'STOCK') return 'Stock (General Inventory)';
    return balagruhas.find((bg) => bg._id === id)?.name || 'N/A';
};

const getItemNames = (items = []) =>
    items.map((entry) => entry.productName || entry.name || entry.shopItemId?.name).filter(Boolean).join(', ') || 'N/A';

const getTotalQuantity = (items = []) =>
    items.reduce((sum, entry) => sum + Number(entry.requestedQuantity || entry.quantity || 0), 0) || 'N/A';

export default function UpdatePurchaseRequestDrawer({ request, balagruhas = [], onClose, onUpdated = () => {} }) {
    const [status, setStatus] = useState(request.status);
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const delivering = deliveringLabel(request);

    const details = [
        ['Item Name', getItemNames(request.items)],
        ['Quantity', getTotalQuantity(request.items)],
        ['Category', request.category || 'N/A'],
        ['Balagruha', getBalagruhaName(request, balagruhas)],
        ['Requested By', requesterWithRole(request) || 'N/A'],
        ['Requested Date', formatDate(request.createdAt)],
        ['Due Date', formatDate(request.deadline)],
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (status !== DELIVERING) return;

        setSubmitting(true);
        const result = await markDelivering(request, note.trim());
        setSubmitting(false);
        if (result.success) {
            showToast(`Status updated: ${delivering}`, 'success');
            onUpdated();
            return;
        }
        // A pending request may have been ordered before the second step failed.
        showToast(
            result.changed
                ? `Marked Ordered, but could not mark ${delivering}: ${result.message}`
                : result.message,
            'error'
        );
        if (result.changed) onUpdated();
    };

    return (
        <div className="upr-overlay" onClick={onClose}>
            <aside
                className="upr-drawer"
                role="dialog"
                aria-modal="true"
                aria-label="Update Purchase Request"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="upr-header">
                    <h3>Update Purchase Request</h3>
                    <button type="button" className="upr-close" onClick={onClose} aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="upr-body">
                        <section className="upr-details">
                            <h4>Item Details (Read Only)</h4>
                            <dl>
                                {details.map(([label, value]) => (
                                    <React.Fragment key={label}>
                                        <dt>{label}</dt>
                                        <dd>{value}</dd>
                                    </React.Fragment>
                                ))}
                            </dl>
                        </section>

                        <div className="upr-field">
                            <label htmlFor="upr-status">
                                Update Status <span className="upr-required">*</span>
                            </label>
                            <select
                                id="upr-status"
                                className="upr-select"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <option value={request.status}>
                                    {CURRENT_STATUS_LABEL[request.status] || request.status}
                                </option>
                                <option value={DELIVERING}>{delivering}</option>
                            </select>
                        </div>

                        <div className="upr-field">
                            <label htmlFor="upr-note">Note (Optional)</label>
                            <textarea
                                id="upr-note"
                                className="upr-textarea"
                                rows={4}
                                maxLength={500}
                                placeholder="Add note here..."
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="upr-footer">
                        <button type="button" className="upr-btn-cancel" onClick={onClose} disabled={submitting}>
                            Cancel
                        </button>
                        <button type="submit" className="upr-btn-save" disabled={submitting || status !== DELIVERING}>
                            {submitting ? 'Updating...' : 'Update'}
                        </button>
                    </div>
                </form>
            </aside>
        </div>
    );
}
